import React from 'react';

import { Checkbox, Flex, Form, Popconfirm } from 'antd-v5';
import type { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { Button, DatePicker, Text } from 'scouting-ui/lib';

import { luxonViewDateFormat } from '@shared/constants';
import { intl } from '@shared/localization';

import type { ClassEntryFormValues, RequirementEntry } from '../types';
import { effectiveDate, isFutureDate } from '../utils';
import styles from './ClassEntry.module.scss';

interface ClassEntryActionBarProps {
  requirementKeys: string[];
  eligibleIds: number[];
  isSaving: boolean;
  onReset: () => void;
  onSave: () => void;
}

export default function ClassEntryActionBar(props: ClassEntryActionBarProps) {
  const { requirementKeys, eligibleIds, isSaving, onReset, onSave } = props;

  const form = Form.useFormInstance<ClassEntryFormValues>();
  const entries: Record<string, RequirementEntry> | undefined = Form.useWatch(
    'requirements',
    { form, preserve: true },
  );
  const completionDate: DateTime | null | undefined = Form.useWatch(
    'completionDate',
    { form, preserve: true },
  );

  const youthCount = (key: string) => entries?.[key]?.youth?.length ?? 0;
  const readyCount = requirementKeys.filter(
    key =>
      youthCount(key) > 0 &&
      effectiveDate(entries?.[key], completionDate) !== null,
  ).length;
  const selectedPairs = requirementKeys.reduce(
    (sum, key) => sum + youthCount(key),
    0,
  );
  const totalPairs = requirementKeys.length * eligibleIds.length;
  const allApproved = totalPairs > 0 && selectedPairs === totalPairs;

  const handleApproveAll = (checked: boolean) =>
    requirementKeys.forEach(key =>
      form.setFieldValue(
        ['requirements', key, 'youth'],
        checked ? eligibleIds : [],
      ),
    );

  return (
    <Flex wrap align="center" gap="14px 26px" className={styles.actionBar}>
      <Flex vertical gap={6}>
        <Flex align="center" gap={14}>
          <Form.Item name="completionDate" noStyle>
            <DatePicker
              id="class-entry-global-date"
              className={styles.datePickerAll}
              placeholder={intl.formatMessage({
                id: 'page.mbcClassEntry.completionDateAll',
              })}
              format={luxonViewDateFormat}
              disabled={isSaving}
              disabledDate={isFutureDate}
            />
          </Form.Item>
          <Checkbox
            checked={allApproved}
            indeterminate={selectedPairs > 0 && !allApproved}
            disabled={isSaving || totalPairs === 0}
            onChange={e => handleApproveAll(e.target.checked)}
          >
            <Text color="color-white">
              <FormattedMessage id="page.mbcClassEntry.approveAll" />
            </Text>
          </Checkbox>
        </Flex>
      </Flex>

      <Flex
        vertical
        align="flex-end"
        gap={8}
        className={styles.actionBarStatus}
      >
        <Text font="body-caption" color="color-white">
          <FormattedMessage
            id="page.mbcClassEntry.readyToSave"
            values={{ ready: readyCount, total: requirementKeys.length }}
          />
        </Text>
        <Flex gap={10} align="center">
          <Popconfirm
            title={intl.formatMessage({
              id: 'page.mbcClassEntry.reset.description',
            })}
            cancelText={intl.formatMessage({
              id: 'page.mbcClassEntry.reset.cancel',
            })}
            cancelButtonProps={{
              type: 'default',
            }}
            onConfirm={onReset}
          >
            <Button color="white" variant="outlined" disabled={isSaving}>
              <FormattedMessage id="page.mbcClassEntry.reset" />
            </Button>
          </Popconfirm>
          <Button
            loading={isSaving}
            disabled={readyCount === 0}
            onClick={onSave}
          >
            <FormattedMessage id="shared.save" />
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
